// Shared by web and native: how the goals list buckets goals by default.
export type GoalPace = 'onPace' | 'behind' | 'quiet'
export type AttentionKey = 'attention' | 'onTrack' | 'done'
export const ATTENTION_ORDER: AttentionKey[] = ['attention', 'onTrack', 'done']

type GoalLike = { _id: string; status?: string; taskCount: number; remainingCount: number }

export function isGoalDone(g: GoalLike): boolean {
  return g.status === 'done' || (g.taskCount > 0 && g.remainingCount === 0)
}

// Default list layout: starred, unfinished goals lead as Focus cards; the rest
// split by what needs attention (behind or quiet), what's on track, and done.
export function attentionLayout<G extends GoalLike>(
  goals: G[],
  starredIds: Set<string>,
  paceOf: (id: string) => GoalPace | null | undefined,
  // Frozen (paused) goals aren't "behind": they sit at the end of On track.
  isFrozen: (g: G) => boolean = () => false,
): { focus: G[]; groups: { key: AttentionKey; goals: G[] }[] } {
  const focus: G[] = []
  const buckets: Record<AttentionKey, G[]> = { attention: [], onTrack: [], done: [] }
  const frozen: G[] = []
  for (const g of goals) {
    if (isGoalDone(g)) buckets.done.push(g)
    else if (isFrozen(g)) frozen.push(g)
    else if (starredIds.has(g._id)) focus.push(g)
    else {
      const pace = paceOf(g._id)
      buckets[pace === 'behind' || pace === 'quiet' ? 'attention' : 'onTrack'].push(g)
    }
  }
  buckets.onTrack.push(...frozen)
  return { focus, groups: ATTENTION_ORDER.map((key) => ({ key, goals: buckets[key] })) }
}
